import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { readApiError } from '../lib/apiError';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { 
  Package, ArrowRight, Truck, Clock, DollarSign, 
  Shield, RefreshCw, Zap, ChevronDown, ChevronUp
} from 'lucide-react';
import AddressBookPicker from '@/components/AddressBookPicker';
import DestinationMapPreview from '@/components/DestinationMapPreview';
import PreLabelAddressReview from '@/components/PreLabelAddressReview';
import DeclaredValueAndCoverage, { COVERAGE_OPTIONS } from '@/components/DeclaredValueAndCoverage';
import MercaderiaCampos, { MERCADERIA_VACIA, faltaMercaderia } from '@/components/MercaderiaCampos';
import { usePrecioCobertura, dinero } from '../lib/precioCobertura';
import AddressAutocomplete from '@/components/AddressAutocomplete';
import DestinationPreview from '@/components/DestinationPreview';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Lo que se cotiza y lo que se compra se arman con las mismas dos funciones: el
// backend compara el envio de la compra con el de la cotizacion y, si cambio,
// pide cotizar de nuevo (services/huella_envio.py, revision del 26-oct).
const direccionDe = (f, lado) => ({
  name: f[`${lado}_name`],
  company: f[`${lado}_company`] || null,
  street1: f[`${lado}_street1`],
  street2: f[`${lado}_street2`] || null,
  city: f[`${lado}_city`],
  state: f[`${lado}_state`],
  zip: f[`${lado}_zip`],
  country: f[`${lado}_country`] || 'US',
  phone: f[`${lado}_phone`] || null,
  email: f[`${lado}_email`] || null,
});

const paqueteDe = (f) => ({
  weight_oz: parseFloat(f.weight_oz),
  length: parseFloat(f.length),
  width: parseFloat(f.width),
  height: parseFloat(f.height),
});

// EasyPost manda la fecha de entrega en ISO (2026-10-30T20:00:00Z) y salia asi en
// pantalla: se muestra el dia. En UTC, para que la hora no la corra al dia anterior.
const DIA_DE_ENTREGA = new Intl.DateTimeFormat('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' });
const fechaDeEntrega = (iso) => {
  if (!iso) return null;  // new Date(null) es el 1-ene-1970
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : DIA_DE_ENTREGA.format(d);
};

// Los campos de una direccion que entran en la huella del envio.
const CAMPOS_DE_LA_HUELLA = ['name', 'company', 'street1', 'street2', 'city', 'state', 'zip', 'country', 'phone'];

const ShippingGatewayPage = () => {
  const { token } = useAuth();
  // Medidas que trae Click Pic, ya revisadas por el comerciante (entrega 53).
  // Se leen una vez: al recargar o volver atras, Rate Shop arranca vacio.
  const estadoDeLaRuta = useLocation().state;
  const [medidasDeClickPic] = useState(() => estadoDeLaRuta?.parcel);
  const [gatewayStatus, setGatewayStatus] = useState(null);
  const [rates, setRates] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [selectedRate, setSelectedRate] = useState(null);
  const [cheapestPerCarrier, setCheapestPerCarrier] = useState(false);
  const [upsGroundSaver, setUpsGroundSaver] = useState(false);
  const [labelResult, setLabelResult] = useState(null);
  const [labelError, setLabelError] = useState(null);
  const [ratesError, setRatesError] = useState(null);
  const [generatingLabel, setGeneratingLabel] = useState(false);
  // Que va adentro, con las categorias de Mike. Reemplaza el campo de texto
  // libre que estaba escondido en "Advanced Options".
  const [goods, setGoods] = useState(MERCADERIA_VACIA);
  const [goodsCfg, setGoodsCfg] = useState(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewRate, setReviewRate] = useState(null);

  const [form, setForm] = useState({
    from_name: 'SelleSend Warehouse',
    from_street1: '4395 Home Ave',
    from_city: 'San Diego',
    from_state: 'CA',
    from_zip: '92105',
    from_country: 'US',
    to_name: '',
    to_street1: '',
    to_city: '',
    to_state: '',
    to_zip: '',
    to_country: 'US',
    to_company: '',
    // Home or business. SmartLGSTX quoted everything as residential.
    to_residential: true,
    weight_oz: 16,
    length: 10,
    width: 8,
    height: 4,
    declared_value: 0,
    signature_required: false,
    coverage: 'A',  // A | B | C | D | none
    ...(medidasDeClickPic && {
      weight_oz: medidasDeClickPic.weight_oz,
      length: medidasDeClickPic.length,
      width: medidasDeClickPic.width,
      height: medidasDeClickPic.height,
    }),
  });

  useEffect(() => {
    if (window.history.state?.usr?.parcel) {
      window.history.replaceState({ ...window.history.state, usr: null }, '');
    }
  }, []);

  useEffect(() => {
    // Sin la sesion daba 403 para todos y los gateways no se mostraban.
    // Si cambia la sesion, lo de la anterior se borra y una respuesta vieja no entra.
    let vigente = true;
    setGatewayStatus(null);
    fetch(`${API_URL}/api/shipping/gateways/status`, {
      headers: { 'Authorization': `Bearer ${token}` },
    })
      .then(res => (res.ok ? res.json() : null))
      .then(data => { if (vigente && data) setGatewayStatus(data); })
      .catch(err => console.error('Gateway status error:', err));
    return () => { vigente = false; };
  }, [token]);

  // Solo los que la API marca activos (al admin le llegan tambien los apagados).
  const gatewaysEnLinea = (gatewayStatus?.gateways || []).filter(gw => gw.status === 'active');

  // Si cambia algo de lo cotizado, las tarifas en pantalla ya no son de este
  // envio: se borran y hay que cotizar de nuevo (revision del 26-oct, D.1).
  const firmaCotizacion = JSON.stringify([
    direccionDe(form, 'from'), direccionDe(form, 'to'), form.to_residential,
    paqueteDe(form), form.declared_value, form.signature_required,
  ]);
  useEffect(() => {
    setRates(null);
    setSelectedRate(null);
  }, [firmaCotizacion]);

  const getRates = async (override) => {
    // El boton pasa el evento: solo se usa `override` cuando es un formulario
    // (al recotizar despues de corregir direcciones).
    const f = override && override.to_zip !== undefined ? override : form;
    setLoading(true);
    setRates(null);
    setSelectedRate(null);
    setLabelResult(null);
    try {
      const res = await fetch(`${API_URL}/api/shipping/rates`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          from_address: direccionDe(f, 'from'),
          to_address: { ...direccionDe(f, 'to'), residential: f.to_residential },
          parcel: paqueteDe(f),
          declared_value: parseFloat(f.declared_value) || 0,
          signature_required: f.signature_required
        })
      });
      if (res.ok) {
        const data = await res.json();
        setRates(data);
        setRatesError(null);
      } else {
        // Sin este else, un fallo al pedir tarifas dejaba la pantalla igual
        // que antes de apretar el boton: sin tarifas, sin error, sin nada.
        let body = null;
        try { body = await res.json(); } catch (_) { /* respuesta sin json */ }
        setRates(null);
        setRatesError(readApiError(res.status, body, 'Could not get rates.'));
      }
    } catch (err) {
      console.error('Rate fetch error:', err);
      setRates(null);
      setRatesError({ status: 0, code: 'network', message: 'Could not reach the server.', extras: {} });
    } finally {
      setLoading(false);
    }
  };

  // La cobertura es una linea del cobro (Mike, 9-oct). No depende de la
  // tarifa elegida, asi que se pide una vez. Sin valor declarado la compra
  // cubre por el limite de 100 (regla 12), y eso es lo que se muestra.
  const coberturaActiva = form.coverage !== 'none';
  const { precio: precioCobertura, error: errorPrecioCobertura } = usePrecioCobertura({
    token,
    tier: (COVERAGE_OPTIONS.find(o => o.id === form.coverage) || {}).tierCode || form.coverage,
    declared: parseFloat(form.declared_value) > 0 ? form.declared_value : 100,
    category: goods.category,
    enabled: coberturaActiva,
  });

  const generateLabel = async (rate) => {
    // Con cobertura, la mercaderia decide si se cubre: se pide antes de abrir
    // la revision de direcciones, no despues de que el servidor la rechace.
    if (form.coverage !== 'none') {
      const falta = faltaMercaderia(goodsCfg, goods);
      if (falta) {
        setLabelResult(null);
        setLabelError({ status: 0, code: 'goods', message: falta, extras: {} });
        return;
      }
    }
    setLabelError(null);
    // Open the address-review modal first
    setReviewRate(rate);
    setReviewOpen(true);
  };

  // The carrier's label, the one that goes on the box. Gateway 1 sends a PDF
  // that needs the session; Gateway 2 answers with a public link. The tab is
  // opened first so the browser does not block it as a pop-up.
  const descargarEtiqueta = async (labelId) => {
    const w = window.open('about:blank');
    try {
      const res = await fetch(`${API_URL}/api/shipping/labels/${labelId}/download`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(typeof data.detail === 'string' ? data.detail : 'The label could not be downloaded.');
      }
      if ((res.headers.get('content-type') || '').includes('application/pdf')) {
        w.location.href = URL.createObjectURL(await res.blob());
      } else {
        w.location.href = (await res.json()).url;
      }
    } catch (err) {
      w.close();
      setLabelError({ status: 0, code: 'download', message: err.message, extras: {} });
    }
  };

  const runLabelGeneration = async (rate, editedFrom, editedTo, requoteRequired) => {
    // Lo corregido en la revision que cambia la huella del envio (no solo el
    // codigo postal) pide cotizar de nuevo: el backend compraria otro envio.
    const cambiaLaHuella = (lado, editado) => CAMPOS_DE_LA_HUELLA.some((k) => {
      const v = editado && editado[k];
      if (v === undefined || v === null || v === '') return false;
      return String(v).trim() !== String(form[`${lado}_${k}`] ?? '').trim();
    });
    if (requoteRequired || cambiaLaHuella('from', editedFrom) || cambiaLaHuella('to', editedTo)) {
      setReviewOpen(false);
      const nuevo = { ...form };
      [['from', editedFrom], ['to', editedTo]].forEach(([lado, editado]) => {
        Object.entries(editado || {}).forEach(([k, v]) => {
          if (v !== undefined && v !== null && v !== '') nuevo[`${lado}_${k}`] = v;
        });
      });
      setForm(nuevo);
      // Antes vaciaba la lista y no volvia a pedir: quedaba "Quote #" sin
      // opciones (revision del 26-oct, E.7).
      getRates(nuevo);
      return;
    }
    setGeneratingLabel(true);
    setReviewOpen(false);
    try {
      // Coverage id IS the backend tier code (A/B/C/D). "none" → skip insurance.
      const covOpt = COVERAGE_OPTIONS.find(o => o.id === form.coverage) || COVERAGE_OPTIONS[1];
      const force_no_insurance = covOpt.id === 'none';

      // What the merchant fixed in the address review counts. It was dropped
      // unless the ZIP changed, so a phone typed there never reached the carrier.
      const conRevision = (base, editado) => {
        const r = { ...base };
        Object.entries(editado || {}).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') r[k] = v; });
        return r;
      };
      const body = {
        rate_id: rate.rate_id,
        gateway: rate.gateway,
        from_address: conRevision(direccionDe(form, 'from'), editedFrom),
        to_address: conRevision(direccionDe(form, 'to'), editedTo),
        parcel: {
          ...paqueteDe(form),
          declared_value: parseFloat(form.declared_value) || 0,
        },
        coverage_tier: covOpt.tierCode || 'A',
        force_no_insurance,
        merchant_plan: covOpt.id,
        channel: 'retail',
        commodity: goods.description.trim() || undefined,
        commodity_category: goods.category || undefined,
        commodity_subtype: goods.subtype || undefined,
        is_fragile: goods.fragile,
        third_party_packed: goods.fragile && goods.thirdParty,
        override_code: goods.code || undefined,
      };
      const res = await fetch(`${API_URL}/api/shipping/quick-label`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (res.ok) {
        setLabelResult(data);
        setLabelError(null);
      } else {
        // Antes esto solo hacia console.error, asi que el usuario apretaba el
        // boton y no pasaba nada visible. Ahora que la compra puede negarse de
        // verdad (sin saldo, sin llave, pasarela no conectada, el transportista
        // fallo) callarse es peor que el error.
        setLabelResult(null);
        setLabelError(readApiError(res.status, data, 'The label could not be created.'));
      }
    } catch (err) {
      console.error('Label generation error:', err);
      setLabelResult(null);
      setLabelError({ status: 0, code: 'network', message: 'Could not reach the server.', extras: {} });
    } finally {
      setGeneratingLabel(false);
    }
  };

  const updateField = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const gatewayBadge = (gw, gwLabel) => {
    // Backend already rebrands as "Gateway N" — prefer that.
    if (gwLabel) return { label: gwLabel, cls: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200' };
    return { label: 'Gateway', cls: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200' };
  };

  return (
    <div className="space-y-6" data-testid="shipping-gateway-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Rate Shop</h1>
          {/* Nombraba UPS y DHL, que hoy no cotizan (§9.159). */}
          <p className="text-slate-500 mt-1">Top rates across live gateways</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {gatewaysEnLinea.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              {gatewaysEnLinea.map(gw => (
                <span key={gw.id} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300" data-testid={`gateway-status-${gw.id}`}>
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                  Gateway {gw.number} · live
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Left - Form (2 cols) */}
        <div className="lg:col-span-2 space-y-5">
          {/* Declared value + coverage (top of form) */}
          <DeclaredValueAndCoverage
            declaredValue={form.declared_value}
            onDeclaredChange={(v) => updateField('declared_value', v)}
            coverage={form.coverage}
            onCoverageChange={(c) => updateField('coverage', c)}
          />

          <MercaderiaCampos value={goods} onChange={setGoods} onConfig={setGoodsCfg}
            destinationCountry={form.to_country || 'US'} />

          {/* From Address */}
          <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-medium text-sm uppercase tracking-wider text-slate-400">Origin</h3>
            </div>
            <AddressBookPicker
              token={token}
              type="ship_from"
              value={{ name: form.from_name, street1: form.from_street1, city: form.from_city, state: form.from_state, zip: form.from_zip }}
              onSelect={(a) => setForm((f) => ({
                ...f,
                from_name: a.name || f.from_name,
                from_company: a.company || '',
                from_street1: a.street1 || f.from_street1,
                from_city: a.city || f.from_city,
                from_state: a.state || f.from_state,
                from_zip: a.zip || f.from_zip,
                from_country: a.country || f.from_country || 'US',
              }))}
              testid="from-addrbook"
            />
            <div className="space-y-3 mt-3">
              <input value={form.from_name} onChange={e => updateField('from_name', e.target.value)} placeholder="Name" className="w-full h-10 px-3 rounded-lg border border-slate-200 dark:border-slate-700 dark:bg-slate-800 text-sm" data-testid="from-name" />
              <AddressAutocomplete
                value={form.from_street1}
                onChange={(v) => updateField('from_street1', v)}
                onResolve={(a) => setForm((f) => ({
                  ...f,
                  from_street1: a.street1 || f.from_street1,
                  from_city: a.city || f.from_city,
                  from_state: a.state || f.from_state,
                  from_zip: a.zip || f.from_zip,
                  from_country: a.country || f.from_country || 'US',
                }))}
                placeholder="Start typing a street address…"
                testid="from-street"
              />
              <div className="grid grid-cols-3 gap-2">
                <input value={form.from_city} onChange={e => updateField('from_city', e.target.value)} placeholder="City" className="h-10 px-3 rounded-lg border border-slate-200 dark:border-slate-700 dark:bg-slate-800 text-sm" data-testid="from-city" />
                <input value={form.from_state} onChange={e => updateField('from_state', e.target.value)} placeholder="State" className="h-10 px-3 rounded-lg border border-slate-200 dark:border-slate-700 dark:bg-slate-800 text-sm" data-testid="from-state" />
                <input value={form.from_zip} onChange={e => updateField('from_zip', e.target.value)} placeholder="ZIP" className="h-10 px-3 rounded-lg border border-slate-200 dark:border-slate-700 dark:bg-slate-800 text-sm" data-testid="from-zip" />
              </div>
            </div>
          </div>

          {/* To Address */}
          <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-medium text-sm uppercase tracking-wider text-slate-400">Destination</h3>
            </div>
            <AddressBookPicker
              token={token}
              type="ship_to"
              value={{ name: form.to_name, street1: form.to_street1, city: form.to_city, state: form.to_state, zip: form.to_zip }}
              onSelect={(a) => setForm((f) => ({
                ...f,
                to_name: a.name || f.to_name,
                to_company: a.company || '',
                to_residential: !a.company,
                to_street1: a.street1 || f.to_street1,
                to_city: a.city || f.to_city,
                to_state: a.state || f.to_state,
                to_zip: a.zip || f.to_zip,
                to_country: a.country || f.to_country || 'US',
              }))}
              testid="to-addrbook"
            />
            <div className="space-y-3 mt-3">
              <input value={form.to_name} onChange={e => updateField('to_name', e.target.value)} placeholder="Recipient Name" className="w-full h-10 px-3 rounded-lg border border-slate-200 dark:border-slate-700 dark:bg-slate-800 text-sm" data-testid="to-name" />
              <AddressAutocomplete
                value={form.to_street1}
                onChange={(v) => updateField('to_street1', v)}
                onResolve={(a) => setForm((f) => ({
                  ...f,
                  to_street1: a.street1 || f.to_street1,
                  to_city: a.city || f.to_city,
                  to_state: a.state || f.to_state,
                  to_zip: a.zip || f.to_zip,
                  to_country: a.country || f.to_country || 'US',
                }))}
                placeholder="Start typing a street address…"
                testid="to-street"
              />
              <div className="grid grid-cols-3 gap-2">
                <input value={form.to_city} onChange={e => updateField('to_city', e.target.value)} placeholder="City" className="h-10 px-3 rounded-lg border border-slate-200 dark:border-slate-700 dark:bg-slate-800 text-sm" data-testid="to-city" />
                <input value={form.to_state} onChange={e => updateField('to_state', e.target.value)} placeholder="State" className="h-10 px-3 rounded-lg border border-slate-200 dark:border-slate-700 dark:bg-slate-800 text-sm" data-testid="to-state" />
                <input value={form.to_zip} onChange={e => updateField('to_zip', e.target.value)} placeholder="ZIP" className="h-10 px-3 rounded-lg border border-slate-200 dark:border-slate-700 dark:bg-slate-800 text-sm" data-testid="to-zip" />
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
                <input type="checkbox" checked={form.to_residential} onChange={e => updateField('to_residential', e.target.checked)} data-testid="to-residential" />
                Residential address
                <span className="text-xs text-slate-400">(uncheck for a business)</span>
              </label>
            </div>
          </div>

          {/* Google Maps destination preview */}
          <DestinationMapPreview
            street={form.to_street1}
            city={form.to_city}
            state={form.to_state}
            zip={form.to_zip}
            country={form.to_country}
            testid="rateshop-destmap"
          />

          {/* Parcel */}
          <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-5">
            <h3 className="font-medium text-sm uppercase tracking-wider text-slate-400 mb-4">Package</h3>
            {medidasDeClickPic && ['weight_oz', 'length', 'width', 'height'].every(k => Number(form[k]) === medidasDeClickPic[k]) && (
              <p className="text-xs text-blue-600 dark:text-blue-400 -mt-2 mb-3" data-testid="medidas-click-pic">
                Size and weight from Click Pic. Check them before you buy the label.
              </p>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Weight (oz)</label>
                <input type="number" min="0" value={form.weight_oz || ''} onChange={e => updateField('weight_oz', e.target.value)} className="w-full h-10 px-3 rounded-lg border border-slate-200 dark:border-slate-700 dark:bg-slate-800 text-sm tabular-nums" data-testid="weight-oz" />
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Length (in)</label>
                <input type="number" min="0" value={form.length || ''} onChange={e => updateField('length', e.target.value)} className="w-full h-10 px-3 rounded-lg border border-slate-200 dark:border-slate-700 dark:bg-slate-800 text-sm tabular-nums" data-testid="length" />
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Width (in)</label>
                <input type="number" min="0" value={form.width || ''} onChange={e => updateField('width', e.target.value)} className="w-full h-10 px-3 rounded-lg border border-slate-200 dark:border-slate-700 dark:bg-slate-800 text-sm tabular-nums" data-testid="width" />
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Height (in)</label>
                <input type="number" min="0" value={form.height || ''} onChange={e => updateField('height', e.target.value)} className="w-full h-10 px-3 rounded-lg border border-slate-200 dark:border-slate-700 dark:bg-slate-800 text-sm tabular-nums" data-testid="height" />
              </div>
            </div>

            <button onClick={() => setShowAdvanced(!showAdvanced)} className="mt-3 text-xs text-blue-600 flex items-center gap-1" data-testid="toggle-advanced">
              {showAdvanced ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              Advanced Options
            </button>
            {showAdvanced && (
              <div className="mt-3 space-y-3 pt-3 border-t border-slate-100 dark:border-slate-800">
                <div>
                  <label className="text-xs text-slate-500 mb-1 block">Declared Value ($)</label>
                  <input type="number" min="0" value={form.declared_value || ''} onChange={e => updateField('declared_value', e.target.value)} className="w-full h-10 px-3 rounded-lg border border-slate-200 dark:border-slate-700 dark:bg-slate-800 text-sm" data-testid="declared-value" />
                </div>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={form.signature_required} onChange={e => updateField('signature_required', e.target.checked)} className="rounded" data-testid="signature-check" />
                  Signature Required
                </label>
                {/* Aca estaba la casilla de seguro: solo le llegaba a Easyship, que no
                    tiene llave, asi que no hacia nada. La cobertura se elige
                    arriba y se cobra como linea (§9.159). */}
              </div>
            )}
          </div>

          {/* Aca estaba el selector de Premium Tools: decia "Adding $X to this
              label" y no sumaba ni cobraba nada, y tres de las cuatro herramientas
              dan 503 (Mike lo encontro, RESPUESTAS_MIKE.md §9.159). Vuelve cuando
              cada herramienta exista y se cobre como linea. */}
          <Button onClick={getRates} disabled={loading || !form.to_zip} className="w-full h-12 text-base font-medium" data-testid="get-rates-btn">
            {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Truck className="w-4 h-4 mr-2" />}
            {loading ? 'Fetching Rates...' : 'Get Shipping Rates'}
          </Button>
          {/* El boton se apagaba sin decir por que y Mike no encontraba donde
              poner el destino desde el celular (§9.159). */}
          {!loading && !form.to_zip && (
            <p className="text-sm text-amber-700 dark:text-amber-400 text-center -mt-2" data-testid="get-rates-missing">
              Add the destination ZIP to get rates.
            </p>
          )}
        </div>

        {/* Right - Results (3 cols) */}
        <div className="lg:col-span-3 space-y-5">
          {/* Destination preview: map + verify + AI fraud */}
          <DestinationPreview
            address={{
              name: form.to_name, company: form.to_company,
              street1: form.to_street1, street2: form.to_street2,
              city: form.to_city, state: form.to_state,
              zip: form.to_zip, country: form.to_country || 'US',
            }}
            declaredValue={form.declared_value}
          />

          {/* Label Result */}
          {labelResult && (
            <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-xl p-5" data-testid="label-result">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center flex-shrink-0">
                  <Shield className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-emerald-800 dark:text-emerald-200">Label Generated</h3>
                  <p className="text-sm text-emerald-600 dark:text-emerald-400 mt-1 font-mono">Tracking: {labelResult.tracking_number}</p>
                  <p className="text-xs text-emerald-500 mt-1">Label ID: {labelResult.label_id}{labelResult.fingerprint_registered ? ' | Fingerprint registered' : ''}</p>
                  {labelResult.charge?.on_credit > 0 && (
                    <p className="text-xs text-emerald-700 dark:text-emerald-300 mt-1" data-testid="label-on-credit">
                      {dinero(labelResult.charge.on_credit)} goes on your weekly Gateway 1 invoice
                      {labelResult.charge.amount > 0 ? `; ${dinero(labelResult.charge.amount)} for coverage was charged to your wallet.` : '.'}
                    </p>
                  )}
                  <Button size="sm" className="mt-3" onClick={() => descargarEtiqueta(labelResult.label_id)} data-testid="download-label">
                    Download label
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* The label was refused. This block did not exist: the page stayed
              exactly as it was and the only trace was a line in the console. */}
          {labelError && (
            <div className="bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800 rounded-xl p-5" data-testid="label-error">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-rose-100 dark:bg-rose-900/40 flex items-center justify-center flex-shrink-0">
                  <Shield className="w-5 h-5 text-rose-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-rose-800 dark:text-rose-200">No label was created</h3>
                  <p className="text-sm text-rose-700 dark:text-rose-300 mt-1">{labelError.message}</p>
                  {labelError.extras?.required !== undefined && (
                    <p className="text-sm text-rose-600 dark:text-rose-400 mt-1">
                      Needs ${labelError.extras.required} and the wallet has ${labelError.extras.available}.
                    </p>
                  )}
                  {labelError.extras?.what_is_missing && (
                    <p className="text-sm text-rose-600 dark:text-rose-400 mt-1">{labelError.extras.what_is_missing}</p>
                  )}
                  <p className="text-xs text-rose-500 mt-2">
                    Nothing was charged.{labelError.code ? ` (${labelError.code})` : ''}
                  </p>
                </div>
              </div>
            </div>
          )}

          {ratesError && (
            <div className="bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800 rounded-xl p-4" data-testid="rates-error">
              <p className="text-sm font-medium text-rose-800 dark:text-rose-200">No rates came back</p>
              <p className="text-sm text-rose-700 dark:text-rose-300 mt-1">{ratesError.message}</p>
            </div>
          )}

          {/* Rates */}
          {rates && (
            <>
              {/* 25-oct: a gateway that did not answer (e.g. USPS rate-limited) used to
                  vanish from the list without a word. Now the page says what is missing. */}
              {(rates.unavailable_gateways || []).length > 0 && (
                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-3" data-testid="rates-unavailable">
                  {rates.unavailable_gateways.map((g) => (
                    <p key={g.gateway_number} className="text-sm text-amber-800 dark:text-amber-200">
                      <b>{g.gateway_number === 2 ? 'USPS' : g.gateway_label}</b> ({g.gateway_label}) is not available right now: {g.message} The options below are from the other gateways.
                    </p>
                  ))}
                </div>
              )}
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold">Quote #{rates.quote_id}</h3>
                  <p className="text-sm text-slate-500">{rates.total_options} options from {((rates.gateways_used || rates.gateway_numbers_used || []) || []).length} gateways</p>
                </div>
                <div className="flex gap-2 items-center flex-wrap">
                  <button
                    onClick={() => setCheapestPerCarrier(v => !v)}
                    className={`text-xs px-2.5 py-1 rounded-full font-medium border transition-colors ${
                      cheapestPerCarrier
                        ? 'bg-indigo-500 text-white border-indigo-500'
                        : 'bg-white dark:bg-slate-900 border-slate-200 text-slate-600 hover:border-indigo-400'
                    }`}
                    data-testid="cheapest-per-carrier-toggle"
                  >
                    {cheapestPerCarrier ? '⚡ Cheapest per carrier' : 'All services'}
                  </button>
                  <button
                    onClick={() => setUpsGroundSaver(v => !v)}
                    className={`text-xs px-2.5 py-1 rounded-full font-medium border transition-colors ${
                      upsGroundSaver
                        ? 'bg-amber-500 text-white border-amber-500'
                        : 'bg-white dark:bg-slate-900 border-slate-200 text-slate-600 hover:border-amber-400'
                    }`}
                    data-testid="ups-ground-saver-toggle"
                    title="Show only economy ground services"
                  >
                    {/* Filtra el ground economico de todos (USPS Ground Advantage,
                        FedEx Ground Economy); decia "UPS Ground Saver" y UPS hoy no
                        cotiza (§9.159). */}
                    {upsGroundSaver ? '🚚 Economy ground only' : 'Economy ground'}
                  </button>
                  {rates.cheapest && (
                    <span className="text-xs px-2.5 py-1 bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300 rounded-full font-medium" data-testid="cheapest-badge">
                      Cheapest: ${rates.cheapest.sell_rate}
                    </span>
                  )}
                  {rates.fastest && (
                    <span className="text-xs px-2.5 py-1 bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 rounded-full font-medium" data-testid="fastest-badge">
                      Fastest: {rates.fastest.delivery_days}d
                    </span>
                  )}
                </div>
              </div>

              <div className="space-y-3">
                {(() => {
                  let ratesToShow = rates.rates || [];
                  if (upsGroundSaver) {
                    ratesToShow = ratesToShow.filter(r => {
                      const carrier = (r.carrier || '').toLowerCase();
                      const svc = (r.service || r.service_name || r.service_type || '').toLowerCase();
                      // UPS-native Ground Saver
                      if (carrier.includes('ups') && (svc.includes('ground saver') || svc.includes('groundsaver'))) return true;
                      // Cross-carrier economy ground equivalents
                      if (svc.includes('ground advantage')) return true;      // USPS
                      if (svc.includes('smartpost') || svc.includes('sure post')) return true;   // FedEx SmartPost, UPS SurePost
                      if (svc.includes('ground economy')) return true;        // FedEx Ground Economy (rebranded SmartPost)
                      return false;
                    });
                  }
                  if (cheapestPerCarrier) {
                    const bestByCarrier = new Map();
                    for (const r of ratesToShow) {
                      const key = (r.carrier || 'unknown').toLowerCase();
                      const price = r.sell_rate ?? r.rate ?? Number.POSITIVE_INFINITY;
                      const cur = bestByCarrier.get(key);
                      if (!cur || price < (cur.sell_rate ?? cur.rate ?? Number.POSITIVE_INFINITY)) {
                        bestByCarrier.set(key, r);
                      }
                    }
                    ratesToShow = Array.from(bestByCarrier.values()).sort((a, b) => (a.sell_rate ?? a.rate) - (b.sell_rate ?? b.rate));
                  }
                  if (ratesToShow.length === 0) {
                    return (
                      <div className="p-6 rounded-lg border-2 border-dashed border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-950/20 text-center text-sm text-amber-700 dark:text-amber-300" data-testid="no-rates-filter">
                        No rates match the current filter. Try toggling off <b>{upsGroundSaver ? 'Economy ground' : 'Cheapest per carrier'}</b>.
                      </div>
                    );
                  }
                  // The list is grouped by speed (Overnight first), so the first
                  // card is not the cheapest one. "Best Price" goes on the lowest.
                  const precio = (r) => r.sell_rate ?? r.rate ?? Number.POSITIVE_INFINITY;
                  const masBarata = ratesToShow.reduce((m, r, j) => (precio(r) < precio(ratesToShow[m]) ? j : m), 0);
                  return ratesToShow.map((rate, i) => {
                  const badge = gatewayBadge(rate.gateway, rate.gateway_label);
                  const isSelected = selectedRate === i;
                  const entrega = fechaDeEntrega(rate.delivery_date);
                  return (
                    <div
                      key={i}
                      onClick={() => setSelectedRate(i)}
                      className={`bg-white dark:bg-slate-900 rounded-xl border p-4 cursor-pointer transition-all ${
                        isSelected ? 'border-blue-500 ring-2 ring-blue-500/20 shadow-md' : 'border-slate-200 dark:border-slate-800 hover:border-slate-300 hover:shadow-sm'
                      }`}
                      data-testid={`rate-card-${i}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                            <Package className="w-5 h-5 text-slate-600 dark:text-slate-300" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-sm">{rate.carrier}</span>
                              <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${badge.cls}`}>{badge.label}</span>
                              {i === masBarata && <span className="text-[10px] px-1.5 py-0.5 rounded font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">Best Price</span>}
                            </div>
                            <p className="text-xs text-slate-500 mt-0.5">{rate.service_pretty || rate.service}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-semibold tabular-nums">${rate.sell_rate?.toFixed(2) || rate.rate?.toFixed(2)}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 mt-3 text-xs text-slate-500">
                        <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {rate.eta_label || (rate.delivery_days ? `${rate.delivery_days} business days` : 'ETA on booking')}</span>
                        <span className="flex items-center gap-1"><DollarSign className="w-3 h-3" /> {rate.currency}</span>
                        {entrega && <span>Est. {entrega}</span>}
                      </div>
                      {isSelected && (
                        <>
                          {/* The protection programme picker showed a price here that the
                              purchase never charged. It comes back when it is charged as a line. */}
                          <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between">
                            <div className="text-xs text-slate-500 space-y-0.5" data-testid={`charge-lines-${i}`}>
                              {/* The carrier's surcharges, when it sends them, each on its own line. */}
                              <div className="flex justify-between gap-6"><span>Shipping</span><b className="tabular-nums">{dinero(rate.surcharges?.length ? rate.shipping_amount : (rate.sell_rate || rate.rate))}</b></div>
                              {(rate.surcharges || []).map((s, j) => (
                                <div key={j} className="flex justify-between gap-6" data-testid={`surcharge-${i}-${j}`}><span>{s.description}</span><b className="tabular-nums">{dinero(s.amount)}</b></div>
                              ))}
                              {rate.admin_fee > 0 && (
                                <div className="flex justify-between gap-6" data-testid={`admin-fee-${i}`}><span>Admin fee</span><b className="tabular-nums">{dinero(rate.admin_fee)}</b></div>
                              )}
                              {coberturaActiva && (
                                <div className="flex justify-between gap-6"><span>Coverage</span><b className="tabular-nums">{precioCobertura ? dinero(precioCobertura.amount) : (errorPrecioCobertura ? 'unavailable' : '…')}</b></div>
                              )}
                              {rate.gateway === 'sellegard' ? (
                                <>
                                  {/* Gateway 1 runs on credit: shipping and admin fee go on the
                                      weekly invoice; only coverage comes out of the wallet now. */}
                                  <div className="flex justify-between gap-6 border-t border-slate-100 dark:border-slate-800 pt-0.5" data-testid={`on-invoice-${i}`}><span>On your weekly invoice</span>
                                    <b className="tabular-nums text-slate-800 dark:text-slate-100">{dinero((rate.sell_rate || rate.rate) + (rate.admin_fee || 0))}</b>
                                  </div>
                                  {coberturaActiva && (
                                    <div className="flex justify-between gap-6"><span>Charged to your wallet now</span>
                                      <b className="tabular-nums text-slate-800 dark:text-slate-100">{precioCobertura ? dinero(precioCobertura.amount) : '…'}</b>
                                    </div>
                                  )}
                                </>
                              ) : (
                                <div className="flex justify-between gap-6 border-t border-slate-100 dark:border-slate-800 pt-0.5"><span>Total charged to your wallet</span>
                                  <b className="tabular-nums text-slate-800 dark:text-slate-100">
                                    {coberturaActiva && !precioCobertura ? '…' : dinero((rate.sell_rate || rate.rate) + (rate.admin_fee || 0) + (coberturaActiva ? precioCobertura.amount : 0))}
                                  </b>
                                </div>
                              )}
                              {coberturaActiva && errorPrecioCobertura && (
                                <div className="text-red-600" data-testid={`coverage-price-error-${i}`}>{errorPrecioCobertura}</div>
                              )}
                            </div>
                            <Button size="sm" onClick={() => generateLabel(rate)} disabled={generatingLabel || (coberturaActiva && !precioCobertura)} data-testid={`buy-label-${i}`}>
                              {generatingLabel ? <RefreshCw className="w-3 h-3 mr-1 animate-spin" /> : <ArrowRight className="w-3 h-3 mr-1" />}
                              {generatingLabel ? 'Generating...' : 'Buy Label'}
                            </Button>
                          </div>
                        </>
                      )}
                    </div>
                  );
                });
                })()}
              </div>
            </>
          )}

          {/* Empty State */}
          {!rates && !loading && (
            <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-12 text-center">
              <div className="w-16 h-16 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center mx-auto mb-4">
                <Truck className="w-8 h-8 text-slate-400" />
              </div>
              <h3 className="font-semibold text-lg">Enter shipment details</h3>
              <p className="text-slate-500 mt-2 text-sm max-w-md mx-auto">
                Fill in the origin, destination, and package details to get live rates.
              </p>
            </div>
          )}

          {loading && (
            <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-12 text-center">
              <RefreshCw className="w-8 h-8 text-blue-500 animate-spin mx-auto mb-4" />
              <p className="text-slate-500">Querying gateways...</p>
            </div>
          )}
        </div>
      </div>

      {/* Pre-print address review modal */}
      <PreLabelAddressReview
        open={reviewOpen}
        onClose={() => setReviewOpen(false)}
        shipmentId={reviewRate?.shipment_id}
        token={token}
        rate={reviewRate}
        initialFrom={{
          name: form.from_name, company: form.from_company, street1: form.from_street1,
          street2: form.from_street2, city: form.from_city, state: form.from_state, zip: form.from_zip,
          country: form.from_country || 'US', phone: form.from_phone,
        }}
        initialTo={{
          name: form.to_name, company: form.to_company, street1: form.to_street1,
          street2: form.to_street2, city: form.to_city, state: form.to_state, zip: form.to_zip,
          country: form.to_country || 'US', phone: form.to_phone,
        }}
        onConfirm={({ from: eFrom, to: eTo, requote_required }) => runLabelGeneration(reviewRate, eFrom, eTo, requote_required)}
      />
    </div>
  );
};

export default ShippingGatewayPage;
